import Phaser from 'phaser';
import type { ThemeConfig, ThemeKey } from '../types';
import { THEMES } from '../config/levels';

export function ensureGroundTextures(scene: Phaser.Scene): void {
  const variants: Array<{
    key: string;
    dirt: number;
    grass: number;
    grassDark: number;
    pebble: number;
  }> = [
    { key: 'ground_meadow', dirt: 0x8b5a2b, grass: 0x4f9d3a, grassDark: 0x3a7d2a, pebble: 0x6b4220 },
    { key: 'ground_forest', dirt: 0x4d3520, grass: 0x2f5d2a, grassDark: 0x1f4220, pebble: 0x2a1d10 },
    { key: 'ground_river', dirt: 0xd9c590, grass: 0xe6d8a4, grassDark: 0xb59f6a, pebble: 0xa86a3a },
    { key: 'ground_pasture', dirt: 0xa8895a, grass: 0x88a83a, grassDark: 0x607a26, pebble: 0x7a5a30 },
    { key: 'ground_mountains', dirt: 0x6a6f80, grass: 0x9ea4b5, grassDark: 0x40455a, pebble: 0xe7ecf5 },
    { key: 'ground_snowy', dirt: 0xeaf2fa, grass: 0xffffff, grassDark: 0xc8d8e8, pebble: 0xb8c8d8 },
    { key: 'ground_desert', dirt: 0xeac98a, grass: 0xf6dfa5, grassDark: 0xc88a4a, pebble: 0x8a5a2a },
    { key: 'ground_jungle', dirt: 0x6b3e1a, grass: 0x4ea36b, grassDark: 0x2a6822, pebble: 0x88a83a },
    { key: 'ground_sky', dirt: 0xeaf2fa, grass: 0xffffff, grassDark: 0xc8d8e8, pebble: 0xc8d8e8 },
    // Carpet (muted teal) — soft pile colour with darker fabric speckle.
    { key: 'ground_house', dirt: 0x6e8a8e, grass: 0x82a0a4, grassDark: 0x4f686b, pebble: 0xa3bcc0 },
  ];

  for (const v of variants) {
    if (scene.textures.exists(v.key)) continue;
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    if (v.key === 'ground_house') {
      // Carpet: solid base + lighter pile speckle + tiny darker flecks.
      g.fillStyle(v.dirt, 1);
      g.fillRect(0, 0, 64, 120);
      // Tiny lighter fabric speckle (pile)
      g.fillStyle(v.grass, 1);
      for (let y = 2; y < 120; y += 3) {
        for (let x = (y % 6 === 0 ? 0 : 3); x < 64; x += 6) {
          g.fillRect(x, y, 1, 1);
        }
      }
      // Scattered darker flecks
      g.fillStyle(v.grassDark, 1);
      g.fillRect(8, 18, 2, 1);
      g.fillRect(40, 32, 2, 1);
      g.fillRect(22, 52, 2, 1);
      g.fillRect(52, 70, 2, 1);
      g.fillRect(14, 88, 2, 1);
      g.fillRect(36, 102, 2, 1);
      // Occasional brighter highlight thread
      g.fillStyle(v.pebble, 0.6);
      g.fillRect(4, 28, 4, 1);
      g.fillRect(28, 76, 4, 1);
      g.fillRect(48, 110, 4, 1);
    } else {
      g.fillStyle(v.dirt, 1);
      g.fillRect(0, 0, 64, 120);
      g.fillStyle(v.grass, 1);
      g.fillRect(0, 0, 64, 10);
      g.fillStyle(v.grassDark, 1);
      for (let i = 0; i < 64; i += 6) {
        g.fillRect(i, 8, 2, 6);
      }
      g.fillStyle(v.pebble, 1);
      g.fillCircle(12, 40, 2);
      g.fillCircle(40, 70, 2);
      g.fillCircle(24, 95, 2);
    }
    g.generateTexture(v.key, 64, 120);
    g.destroy();
  }
}

export interface ThemeBackgroundParts {
  sky: Phaser.GameObjects.Graphics;
  hills: Phaser.GameObjects.TileSprite | null;
  decorations: Phaser.GameObjects.TileSprite | null;
  groundTile: Phaser.GameObjects.TileSprite;
  cloudGroup: Phaser.GameObjects.Group;
}

export function applyTheme(
  scene: Phaser.Scene,
  themeKey: ThemeKey,
  groundY: number,
): ThemeBackgroundParts {
  const theme: ThemeConfig = THEMES[themeKey];
  const W = scene.scale.width;
  const H = scene.scale.height;

  const sky = scene.add.graphics();
  sky.fillGradientStyle(theme.skyTop, theme.skyTop, theme.skyBottom, theme.skyBottom, 1);
  sky.fillRect(0, 0, W, H);

  let hills: Phaser.GameObjects.TileSprite | null = null;
  if (themeKey !== 'river' && themeKey !== 'house') {
    const hillsKey = `hills_${themeKey}`;
    if (!scene.textures.exists(hillsKey)) {
      const g = scene.make.graphics({ x: 0, y: 0 }, false);
      const tileW = 1600;
      const tileH = 280;
      g.fillStyle(theme.hillShadow, 1);
      g.fillEllipse(300, tileH - 10, 700, 260);
      g.fillEllipse(1100, tileH, 600, 220);
      g.fillStyle(theme.hillColor, 1);
      g.fillEllipse(200, tileH, 600, 220);
      g.fillEllipse(800, tileH - 10, 700, 240);
      g.fillEllipse(1400, tileH + 10, 500, 200);
      // Distant tiny trees on the hilltops, varied by theme.
      drawDistantTrees(g, themeKey, tileW, tileH);
      g.generateTexture(hillsKey, tileW, tileH);
      g.destroy();
    }
    hills = scene.add
      .tileSprite(0, groundY - 250, W, 280, hillsKey)
      .setOrigin(0, 0);
  }

  const groundTile = scene.add
    .tileSprite(0, groundY, W, H - groundY, theme.groundTextureKey)
    .setOrigin(0, 0);

  const cloudGroup = scene.add.group();
  const cloudCount = 4;
  for (let i = 0; i < cloudCount; i++) {
    // Evenly spaced across the screen, plus a tiny jitter so they don't look
    // mechanical.
    const x = (i + 0.5) * (W / cloudCount) + Phaser.Math.Between(-30, 30);
    spawnCloud(scene, cloudGroup, x, theme.cloudColor, theme.cloudAlpha);
  }

  // Decorations: a tile-sprite that scrolls faster than hills, slower than ground.
  const decorations = makeDecorationsLayer(scene, theme, themeKey, groundY);

  return { sky, hills, decorations, groundTile, cloudGroup };
}

function makeDecorationsLayer(
  scene: Phaser.Scene,
  theme: ThemeConfig,
  themeKey: ThemeKey,
  groundY: number,
): Phaser.GameObjects.TileSprite | null {
  const decoKey = `deco_${themeKey}`;
  const tileW = 800;
  const tileH = 130;
  if (!scene.textures.exists(decoKey)) {
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    // Match the original drawDecorations art, but render onto the tile texture
    // so it can repeat seamlessly.
    const baseY = tileH - 6; // ground level inside the tile
    switch (theme.decoration) {
      case 'trees': {
        // Mixed shapes: round leafy, pine, bush.
        const shapes: Array<'leafy' | 'pine' | 'bush'> = ['leafy', 'pine', 'bush'];
        const layout = randomLayout(tileW, 7, 0xdeadbeef);
        for (let idx = 0; idx < layout.length; idx++) {
          const t = layout[idx];
          const shape = shapes[Math.floor(t.r * shapes.length) % shapes.length];
          const sz = 0.6 + t.r2 * 0.9; // 0.6..1.5
          if (shape === 'leafy') {
            const trunkH = 38 * sz;
            const canopyR = 22 * sz;
            g.fillStyle(0x6b3e1a, 1);
            g.fillRect(t.x - 4 * sz, baseY - trunkH, 8 * sz, trunkH);
            g.fillStyle(0x3a7d2a, 1);
            g.fillCircle(t.x, baseY - trunkH - canopyR * 0.5, canopyR);
            g.fillStyle(0x4f9d3a, 1);
            g.fillCircle(t.x - canopyR * 0.4, baseY - trunkH - canopyR * 0.3, canopyR * 0.7);
            g.fillCircle(t.x + canopyR * 0.4, baseY - trunkH - canopyR * 0.3, canopyR * 0.7);
          } else if (shape === 'pine') {
            const treeH = 70 * sz;
            const halfW = 18 * sz;
            g.fillStyle(0x2a6a2a, 1);
            g.fillTriangle(t.x, baseY - treeH, t.x - halfW, baseY, t.x + halfW, baseY);
            g.fillStyle(0x4f9d3a, 1);
            g.fillTriangle(t.x, baseY - treeH * 0.55, t.x - halfW * 0.7, baseY - treeH * 0.1, t.x + halfW * 0.7, baseY - treeH * 0.1);
          } else {
            // Bush
            const r = 16 * sz;
            g.fillStyle(0x3a7d2a, 1);
            g.fillCircle(t.x, baseY - r * 0.6, r);
            g.fillCircle(t.x - r * 0.6, baseY - r * 0.4, r * 0.7);
            g.fillCircle(t.x + r * 0.6, baseY - r * 0.4, r * 0.7);
          }
        }
        break;
      }
      case 'pines':
        for (const t of randomLayout(tileW, 7, 0xfeedbeef)) {
          const sz = 0.5 + t.r * 1.1; // 0.5..1.6
          const treeH = 90 * sz;
          const halfW = 22 * sz;
          g.fillStyle(0x1f4220, 1);
          g.fillTriangle(t.x, baseY - treeH, t.x - halfW, baseY, t.x + halfW, baseY);
          g.fillStyle(0x2a5a30, 1);
          g.fillTriangle(t.x, baseY - treeH * 0.6, t.x - halfW * 0.7, baseY - treeH * 0.1, t.x + halfW * 0.7, baseY - treeH * 0.1);
        }
        break;
      case 'reeds':
        // Ocean — no decoration. Generate empty (transparent) texture.
        break;
      case 'rocks':
        for (let i = 0; i < 4; i++) {
          const x = (i * tileW) / 4 + 100;
          g.fillStyle(0x7a5a30, 1);
          g.fillEllipse(x, baseY - 12, 50, 22);
          g.fillStyle(0x6b4220, 1);
          g.fillEllipse(x + 4, baseY - 10, 26, 12);
        }
        break;
      case 'snowcaps':
        g.fillStyle(0xe7ecf5, 1);
        g.fillTriangle(180, baseY - 60, 80, baseY, 280, baseY);
        g.fillTriangle(580, baseY - 80, 480, baseY, 680, baseY);
        g.fillStyle(0xffffff, 1);
        g.fillTriangle(180, baseY - 60, 158, baseY - 24, 200, baseY - 24);
        g.fillTriangle(580, baseY - 80, 558, baseY - 44, 600, baseY - 44);
        break;
      case 'snow':
        // Pine trees with snow caps + snow drifts
        for (let i = 0; i < 5; i++) {
          const x = (i * tileW) / 5 + 80;
          g.fillStyle(0x2a5a30, 1);
          g.fillTriangle(x, baseY - 80, x - 22, baseY - 30, x + 22, baseY - 30);
          g.fillStyle(0xffffff, 1);
          g.fillTriangle(x, baseY - 80, x - 12, baseY - 56, x + 12, baseY - 56);
        }
        g.fillStyle(0xeaf2fa, 0.8);
        for (let i = 0; i < 4; i++) {
          const x = (i * tileW) / 4 + 50;
          g.fillEllipse(x, baseY - 6, 90, 14);
        }
        break;
      case 'cactus':
        for (let i = 0; i < 4; i++) {
          const x = (i * tileW) / 4 + 100;
          g.fillStyle(0x4a8a3a, 1);
          g.fillRect(x - 6, baseY - 50, 12, 50);
          g.fillRect(x - 18, baseY - 30, 8, 18);
          g.fillRect(x - 18, baseY - 38, 4, 12);
          g.fillRect(x + 10, baseY - 35, 8, 18);
          g.fillRect(x + 14, baseY - 43, 4, 12);
          // Spikes
          g.fillStyle(0xeaf2fa, 0.8);
          for (let yy = 0; yy < 5; yy++) {
            g.fillRect(x - 1, baseY - 48 + yy * 10, 2, 1);
          }
        }
        break;
      case 'jungle': {
        // Mixed: palms (varied heights), ferns (varied sizes), short bushes.
        const layout = randomLayout(tileW, 6, 0xc0ffeebe);
        for (const t of layout) {
          const sz = 0.7 + t.r * 0.9;
          const trunkH = 80 * sz;
          // Palm trunk
          g.fillStyle(0x6b3410, 1);
          g.fillRect(t.x - 4 * sz, baseY - trunkH, 8 * sz, trunkH);
          // Fronds
          g.fillStyle(0x2a6822, 1);
          for (let l = 0; l < 5; l++) {
            const a = (l / 4) * Math.PI - Math.PI / 2;
            g.fillTriangle(
              t.x,
              baseY - trunkH,
              t.x + Math.cos(a) * 30 * sz,
              baseY - trunkH + Math.sin(a) * 12 * sz,
              t.x + Math.cos(a) * 26 * sz,
              baseY - trunkH - 12 * sz + Math.sin(a) * 10 * sz,
            );
          }
        }
        // Ferns scattered along the ground at varying sizes
        const ferns = randomLayout(tileW, 12, 0xfeed0001);
        g.fillStyle(0x4ea36b, 1);
        for (const f of ferns) {
          const sz = 0.7 + f.r2 * 1.1;
          g.fillTriangle(f.x, baseY - 18 * sz, f.x - 10 * sz, baseY, f.x + 10 * sz, baseY);
        }
        // A few short bushes
        const bushes = randomLayout(tileW, 5, 0xbabeface);
        g.fillStyle(0x3a8a3a, 1);
        for (const b of bushes) {
          const r = 12 + b.r * 14;
          g.fillCircle(b.x, baseY - r * 0.5, r);
          g.fillCircle(b.x - r * 0.5, baseY - r * 0.3, r * 0.7);
          g.fillCircle(b.x + r * 0.5, baseY - r * 0.3, r * 0.7);
        }
        break;
      }
      case 'clouds':
        // Big puffy cloud platforms in the distance
        for (let i = 0; i < 4; i++) {
          const x = (i * tileW) / 4 + 80;
          g.fillStyle(0xffffff, 0.9);
          g.fillCircle(x - 20, baseY - 30, 22);
          g.fillCircle(x + 20, baseY - 30, 22);
          g.fillCircle(x, baseY - 38, 26);
          g.fillCircle(x, baseY - 24, 22);
        }
        break;
      case 'toys':
        // Skirting board + a few toys scattered
        g.fillStyle(0xfaf3e1, 1);
        g.fillRect(0, baseY - 14, tileW, 14);
        g.fillStyle(0xc69870, 1);
        g.fillRect(0, baseY - 16, tileW, 2);
        // A teddy
        const tx1 = 120;
        g.fillStyle(0x8a5a2a, 1);
        g.fillCircle(tx1, baseY - 24, 14);
        g.fillCircle(tx1 - 10, baseY - 36, 7);
        g.fillCircle(tx1 + 10, baseY - 36, 7);
        g.fillStyle(0xeac98a, 1);
        g.fillCircle(tx1, baseY - 22, 6);
        // A toy car
        const cx = 320;
        g.fillStyle(0xc23b3b, 1);
        g.fillRect(cx - 22, baseY - 22, 44, 12);
        g.fillRect(cx - 14, baseY - 30, 28, 10);
        g.fillStyle(0x111111, 1);
        g.fillCircle(cx - 14, baseY - 8, 5);
        g.fillCircle(cx + 14, baseY - 8, 5);
        // A building block
        const bx = 500;
        g.fillStyle(0x3a8a3a, 1);
        g.fillRect(bx - 14, baseY - 22, 28, 22);
        g.fillStyle(0xffffff, 1);
        g.fillCircle(bx, baseY - 11, 5);
        // A ball
        const ballx = 670;
        g.fillStyle(0xffd24a, 1);
        g.fillCircle(ballx, baseY - 12, 11);
        g.fillStyle(0xc23b3b, 1);
        g.fillRect(ballx - 11, baseY - 14, 22, 4);
        break;
    }
    g.generateTexture(decoKey, tileW, tileH);
    g.destroy();
  }
  if (theme.decoration === 'reeds') return null;
  return scene.add
    .tileSprite(0, groundY - tileH + 6, scene.scale.width, tileH, decoKey)
    .setOrigin(0, 0);
}

// Deterministic pseudo-random layout: returns N points along [margin..tileW-margin]
// with two random values per point (for size, shape variety). Seeded so the same
// theme produces the same texture every run.
function randomLayout(
  tileW: number,
  count: number,
  seed: number,
): Array<{ x: number; r: number; r2: number }> {
  const rng = mulberry32(seed);
  const margin = 60;
  const span = tileW - margin * 2;
  const points: Array<{ x: number; r: number; r2: number }> = [];
  for (let i = 0; i < count; i++) {
    // Even-spaced base x with random jitter so they're varied but never overlap.
    const baseX = margin + ((i + 0.5) * span) / count;
    const jitter = (rng() - 0.5) * (span / count) * 0.6;
    points.push({ x: baseX + jitter, r: rng(), r2: rng() });
  }
  return points;
}

function mulberry32(a: number): () => number {
  return () => {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Tiny, distant trees painted directly onto the hills texture so the parallax
// hill layer carries some greenery too.
function drawDistantTrees(
  g: Phaser.GameObjects.Graphics,
  themeKey: ThemeKey,
  tileW: number,
  tileH: number,
): void {
  // Pick a tree color that fits the theme; some themes skip distant trees.
  let color: number | null = null;
  switch (themeKey) {
    case 'meadow':
    case 'pasture':
      color = 0x2a6822;
      break;
    case 'forest':
    case 'jungle':
      color = 0x1f4220;
      break;
    case 'snowy':
    case 'mountains':
      color = 0x355a30;
      break;
    case 'desert':
      color = 0x6a8a3a; // sparse dry shrubs
      break;
    default:
      color = null;
  }
  if (color === null) return;
  const points = randomLayout(tileW, 14, 0xa0a0bead);
  // Hilltop band — distant trees sit roughly along the top edge of the hill silhouette.
  const hillTopY = tileH - 60;
  g.fillStyle(color, 1);
  for (const p of points) {
    const sz = 0.4 + p.r * 0.7; // small
    const treeH = 22 * sz;
    const halfW = 7 * sz;
    g.fillTriangle(p.x, hillTopY - treeH, p.x - halfW, hillTopY, p.x + halfW, hillTopY);
  }
}

export function spawnCloud(
  scene: Phaser.Scene,
  group: Phaser.GameObjects.Group,
  x: number,
  color: number,
  alpha: number,
): void {
  const y = Phaser.Math.Between(60, 220);
  const c = scene.add.container(x, y);
  c.add([
    scene.add.circle(0, 0, 22, color, alpha),
    scene.add.circle(20, -6, 18, color, alpha),
    scene.add.circle(-20, -4, 16, color, alpha),
    scene.add.circle(8, 8, 16, color, alpha),
  ]);
  group.add(c);
}

